import { cx } from '@linaria/core';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { COOKIE_SIGNUP_TRACKER } from '../../../constants';
import { SecureStateType } from '../../../constants/enums';
import { locales } from '../../../locales/en';
import { SignupFields, SignupPostFields, SignupResponse, ValidateSignupFields, ValidateSignupResponse, fetchRandomUsername, handleKnownSignup, signupPost, validateSignupPost } from '../../../mfcLogic/auth';
import { getLens, trackLoginInteraction } from '../../../mfcLogic/lens';
import { isLevelGuest } from '../../../mfcLogic/userProperties/level';
import { openAuthenticationDrawer } from '../../../state/modals/state.drawer';
import { setAppState } from '../../../state/state.app';
import { onInputBlur, onInputFocus } from '../../../state/state.layout';
import { registerTimer } from '../../../state/state.timers';
import { trackSignupConversion } from '../../../utilities/analytics';
import { getPromotionalBannerText } from '../../../utilities/bannerText';
import { setCookie } from '../../../utilities/cookies';
import { routerPush } from '../../../utilities/router';
import { safeObj } from '../../../utilities/safe';
import Button from '../../Buttons/Button';
import { AppStateType, useAppState, useAppStateSelector } from '../../Hooks/useAppState';
import { useSecureState } from '../../Hooks/useSecureState';
import { useUserSelector } from '../../Hooks/useUser';
import Eye from '../../Icons/Eye';
import EyeOff from '../../Icons/EyeOff';
import MagicWand from '../../Icons/MagicWand';
import Alert from '../../common/Alert';
import Checkbox from '../../common/Checkbox';
import { StyledWarningText } from '../../common/Forms';
import { StyledDivider } from '../../common/StyledDivider';
import TextField from '../../common/TextField';
import Link from '../Link';
import AgeGateHeader from '../Modals/common/AgeGateHeader';
import Login from './Login';
import SignupSuccess from './SignupSuccess';
import SignupVerified from './SignupVerified';
import ThirdPartyProviders from './ThirdPartyProviders';
import { eyeIconStyle, smallEyeIconStyle, validationWarningStyles } from './shared';
import "./SignUp.linaria.module.css!=!./SignUp?./SignUp.linaria.module.css?css=H4sIAAAAAAAAA41W246bMBR871esVFVqH1jZECDLvvRPLALGMRib2CeQxOq/V4Ql2N6t0pdI43Mm45zLOK8l9OSYkAYYF/Grh2xfasZlBGoo9sPl/c+3e1xQgqFibcoCaIeyrrlk0UEBqL6IdxupMgQ3E0puLIC2URKiiXJ2hCJHaCWwm6eywfVWgjbgSRyRx9jgytB3De9We8KhwwJ8tNzJ8Bst0Gue0n7N1wnBx0y155sFeoGoFJzJoqISqHbK4xTzgcLiOAUdrwQrc1LxaCdew7HACP1Yg31HjnHft5kdlOHAlSw0FSXwkbqKWXMSBx+FitjtoZrrf5EjDeCDNDcdx17XeTzBbuejUCTzNFyGChmzQuYL1EPDdj4KBR4DIjovvwvz7/OxZQsvW4TZy2w80tuE1IxL6G1fXqKlKylC99v+7mnNy5efW+Qlm0O/7J2KETtL2bpyT4+8mqD3P89FWnmqB+Z947OjLyr5XCe5ok4bd6KfHn2yjacqKpmydnC/8cnJKuHukuNO7mTTT5O9roMz3G+GYLhMfBS25mYQ5bVoBH2ES01GPVb04iM750Q117Ra9lJNK+NACWZ7xLIsgAtn0uVQzB8Ptzu5v/WBvvqdmz0eDKJ1HEDPHiOHgVUXX8fUq05w9mKGUjrm55YIZ7dKo4xgmh5PplxSHROc9b5ejl3uzFMF6ShSgt8OoxrU/559uhhyOt53xICc9vvNIcuDUeIMD4d8awmIjiJhq7M2SheD4q5n54Lg/rA/C2krJZQuvud5vgZlQuilgv3BLiaBHdOCjtSi219Ge/dL52mRyep3C8snfYQCC5QJwWN2yzB8kHKPtMZmVu49Ym5PN/iPTjaa4Cl+U7q2ggJQHZmhrLhkBXrFqfOerm+dj+y93xEH2pvg6Wsqj7JB254N8OYaVUoClfD5ycTX3U6JJoDhzjr1bRNy7OCquOPQuw+HXv/G4OnMjEwD6PpTeQbl7ODSZx8FO+h2f946R2KD3hK6GvOfEvdSKGQsff+g/AULVe98nQkAAA=="
const embeddedButtonStyles = "atm_h3_ftgil2";
const errorMessageStyles = "atm_le_1tcgj5g atm_cs_1fw03zg atm_gz_1tcgj5g atm_h0_1tcgj5g";
const noticeStyles = "atm_c8_itk1lt atm_r3_1h6ojuz atm_le_ftgil2 atm_vy_1osqo2v";
const passwordWrapperStyles = "atm_mk_h2mmj6 atm_vy_1osqo2v";
const dividerStyles = "atm_le_f6fqlb atm_c8_itk1lt atm_r3_1h6ojuz atm_lo_1fwxnve atm_vy_1osqo2v";
const stylesEmbeddedBottomPadding = "atm_le_i2wt44";
const rootStyles = "atm_lo_i2wt44 atm_le_idpfg4 atm_lk_idpfg4 atm_ll_idpfg4 atm_j3_dgintm atm_10gunnj_idpfg4 atm_1jnqdpg_idpfg4 atm_13y0krs_ftgil2 atm_o3w6jp_ftgil2";
const ageGatedRootStyles = "atm_lo_1fwxnve atm_le_1fwxnve atm_lk_idpfg4 atm_ll_idpfg4 atm_j3_dgintm";
const formStyles = "atm_vy_1osqo2v atm_9s_1txwivl atm_ar_vrvcex atm_be_1g80g66";
const consentStyles = "atm_gq_ftgil2 atm_gz_1bs0ed2 atm_1ok2yv5_1fwxnve atm_16zcr06_1e5hqsa atm_1ct5vl5_19bvopo";
const buttonSubmitStyles = "atm_vy_1osqo2v";
const magicIconStyle = "atm_mk_stnw88 atm_9j_tlke0l atm_7l_1mb8uln atm_n3_exct8b atm_tk_dlk8xv";
const stylesInnerForm = "atm_mk_h2mmj6 atm_vy_1osqo2v";
const stylesEmbeddedMagicIconStyle = "atm_n3_i2wt44 atm_tk_i2wt44";
const stylesSmallEyeIcon = "atm_n3_1v6z61t atm_tk_1v6z61t";
const loginStyles = "atm_c8_1fwxnve atm_fr_1w29ord";
const loginContainerStyles = "atm_h3_ftgil2 atm_h_1h6ojuz atm_fc_1h6ojuz atm_9s_1txwivl atm_vy_1osqo2v atm_le_1y44olf atm_c8_1fwxnve";
const alertWrapperStyles = "atm_j3_hktyoi atm_h3_1wugsn5 atm_gq_exct8b atm_gz_1wugsn5 atm_h0_1wugsn5";
const bannerContentStyles = "atm_vy_1osqo2v";
type InputType = 'username' | 'password' | 'email';
type StateKey = 'consent' | 'email' | 'username' | 'new_password' | 'skipEmailValidation';

// from MfcSignup/MfcSignup.js
const isValidResponseObj = function (response) {
  return response !== null && typeof response === 'object' &&
  // eslint-disable-next-line no-prototype-builtins
  response.hasOwnProperty('result');
};
interface SignupProps {
  embedded?: boolean;
  showPromoBanner?: boolean;
  onLogin?(): void;
}
const SignUp = memo((props: SignupProps) => {
  const {
    embedded,
    showPromoBanner,
    onLogin
  } = props;
  const authenticated = useAppStateSelector(AppStateType.APP, 'authenticated');
  const {
    ageGateActive
  } = useSecureState(SecureStateType.AGE_VERIFICATION);
  const authState = useAppState(AppStateType.AUTHENTICATION);
  const {
    errors: {
      signup: errors = {}
    } = {},
    success,
    verified
  } = authState;
  const [consent, setConsent] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [new_password, setNewPassword] = useState('');
  const [skipEmailValidation, setSkipEmailValidation] = useState(false);
  const [masked, setMasked] = useState(true);
  const onToggleMasked = useCallback(() => setMasked(!masked), [masked]);
  const level = useUserSelector('me', 'level');
  const isGuest = isLevelGuest(level);
  const previous = useRef({
    consent,
    email,
    username,
    password: new_password,
    new_password,
    skipEmailValidation
  });
  const state = {
    consent,
    email,
    username,
    password: new_password,
    new_password,
    skipEmailValidation
  };
  const setState = (name: StateKey, value: any) => {
    switch (name) {
      case 'consent':
        return setConsent(value);
      case 'email':
        return setEmail(value);
      case 'username':
        return setUsername(value);
      case 'new_password':
        return setNewPassword(value);
    }
  };
  const isValid = (type?: InputType) => {
    if (!type) {
      const {
        username: unErrors = [],
        password: psErrors = [],
        email: eErrors = []
      } = errors;
      return !!((skipEmailValidation || !eErrors.length) && !unErrors.length && !psErrors.length && new_password && username && consent && email);
    }
    const {
      [type]: messages = []
    } = errors;
    if (!messages.length) return true;
    if (type === 'email' && (skipEmailValidation || !email)) return true;
    if (type === 'password' && !new_password) {
      return true;
    }
    if (type === 'username' && !username) return true;
    return false;
  };
  const onSignupResponse = (payload: SignupResponse | ValidateSignupResponse) => {
    try {
      const {
        errmsg,
        result
      } = safeObj(payload);
      if (typeof result === 'object' && result?.sso_bypass && result?.code) {
        getLens()?.Signup_Success({
          ssoBypass: true
        });
        handleKnownSignup(result);
        return;
      }
      const success = result === 'verification_email_sent';
      setAppState(AppStateType.AUTHENTICATION, {
        errors: {
          signup: errmsg
        },
        success
      });
      if (success) {
        setCookie(COOKIE_SIGNUP_TRACKER, 'true');
        getLens()?.Signup_Success();
        trackSignupConversion();
      }
    } catch (ex) {
      // handling edge cases where we do not get a valid payload/response
      // taken from MfcSignup/MfcSignup.js
      console.warn('[onSignupResponse]: ', ex);
    }
  };
  const validateSignup = (name: SignupFields, fields: ValidateSignupFields) => {
    validateSignupPost(name, fields).then(response => {
      onSignupResponse(response);
    });
  };
  const signup = (fields: SignupPostFields) => {
    if (!fields) return;
    getLens()?.Signup_Submit();
    signupPost(fields).then(signupResponse => {
      if (!isValidResponseObj(signupResponse)) {
        setAppState(AppStateType.AUTHENTICATION, {
          errors: {
            signup: {
              general: ['There was a problem with your request. Please try again in a few minutes.']
            }
          },
          success: false
        });
        return;
      }
      if (signupResponse.err > 0) {
        // warn the user
        onSignupResponse(signupResponse);
      }
      signupSetupInterval(signupResponse);
    });
  };
  const signupSetupInterval = (response: SignupResponse) => {
    onSignupResponse(response);
    const {
      result
    } = response;
    if (result !== 'verification_email_sent') {
      return;
    }
    registerTimer('signup', () => {
      if (!authenticated) return;
      setAppState(AppStateType.AUTHENTICATION, {
        verified: true
      });
      routerPush('/');
    }, 1000);
  };
  const onRandomUsername = () => {
    fetchRandomUsername().then(res => {
      console.log('res', res);
      onInputBlur();
      validateSignup('username', {
        ...state,
        username: res.username
      });
      setState('username', res.username);
    });
  };
  const onBlur = (name: Exclude<StateKey, 'consent' | 'skipEmailValidation'>) => () => {
    onInputBlur();
    const {
      [name]: current
    } = state;
    const {
      [name]: last
    } = previous.current;
    if (last === current) return;
    validateSignup(name, state);
    previous.current = {
      ...previous.current,
      [name]: current
    };
  };
  const onChange = (name: StateKey) => event => {
    const {
      target
    } = event;
    if (name === 'consent') {
      const {
        checked
      } = target;
      setConsent(checked);
      return;
    }
    const {
      value
    } = target;
    if (name === 'email') {
      setSkipEmailValidation(true);
    }
    setState(name, value);
  };
  const onSignup = (event = null) => {
    event?.preventDefault();
    trackLoginInteraction(true);
    if (!isValid()) return;
    signup(state);
    setSkipEmailValidation(false);
  };
  const onEnter = event => {
    const {
      key
    } = event;
    if (key !== 'Enter') return;
    event.preventDefault();
    onSignup();
  };
  const validate = (type: InputType) => {
    if (isValid(type)) {
      return null;
    }
    const {
      [type]: messages = []
    } = errors;
    return messages.map((message, index) => <div key={index} className={validationWarningStyles}>
                {'* ' + message}
            </div>);
  };
  const errMessages = useMemo(() => {
    return errors.general || [];
  }, [errors]);
  if (authenticated && !success && !verified) {
    return <Login />;
  }
  if (verified) {
    return <SignupVerified />;
  }
  if (success) {
    return <SignupSuccess email={email} />;
  }
  return <div className={embedded ? `height: 100%; width: 100%;` : ageGateActive ? ageGatedRootStyles : rootStyles}>
            {!embedded && ageGateActive && <AgeGateHeader />}
            {!ageGateActive && !authenticated && showPromoBanner && <div className={alertWrapperStyles}>
                    <Alert severity={'success'} icon={false} centerText>
                        <div className={bannerContentStyles}>
                            {getPromotionalBannerText(isGuest)}
                        </div>
                    </Alert>
                </div>}
            <ThirdPartyProviders embedded={embedded} signup />
            <div className={cx(dividerStyles, embedded && stylesEmbeddedBottomPadding)}>
                <StyledDivider />
            </div>
            <form data-testid="SignUpModal__Form" onKeyUp={onEnter} noValidate className={formStyles}>
                <div className={stylesInnerForm}>
                    <TextField small={embedded} label={locales.signup.username} data-testid="SignUpModal__UsernameInput" helperText={validate('username')} onChange={onChange('username')} error={!isValid('username')} onBlur={onBlur('username')} onFocus={onInputFocus} autoComplete="username" value={username} required fullWidth spaced />
                    <MagicWand className={cx(magicIconStyle, embedded && stylesEmbeddedMagicIconStyle)} data-testid="Icon__GenerateUsername" title="Generate Random Username" onClick={onRandomUsername} />
                </div>
                <TextField small={embedded} label={locales.signup.email} data-testid="SignUpModal__EmailInput" helperText={validate('email')} onChange={onChange('email')} error={!isValid('email')} onBlur={onBlur('email')} onFocus={onInputFocus} type="email" required fullWidth spaced />
                <div className={passwordWrapperStyles}>
                    <TextField small={embedded} label={locales.signup.password} data-testid="SignUpModal__PasswordInput" helperText={validate('password')} onChange={onChange('new_password')} error={!isValid('password')} onBlur={onBlur('new_password')} onFocus={onInputFocus} autoComplete="new-password" type={masked ? 'password' : 'text'} required fullWidth spaced />
                    {masked ? <Eye className={cx(embedded && smallEyeIconStyle, embedded && stylesSmallEyeIcon, !embedded && eyeIconStyle)} onClick={onToggleMasked} /> : <EyeOff onClick={onToggleMasked} className={eyeIconStyle} />}
                </div>
                <Checkbox controlTestId="SignUpModal__Checkbox" onChange={onChange('consent')} checked={consent} value="given" label={<span>
                            {locales.signup.consent}
                            <Link href="https://wiki.myfreecams.com/wiki/Terms_and_Conditions" inline mfc>
                                {locales.signup.terms}
                            </Link>
                            {' and '}
                            <Link href="https://wiki.myfreecams.com/wiki/Privacy_Policy" inline mfc>
                                {locales.signup.privacyPolicy}
                            </Link>
                            .
                        </span>} className={consentStyles} />
                <div className={noticeStyles}>
                    You&apos;ll receive a registration email. We never share
                    your email.
                </div>
                {errMessages.length > 0 && <div data-testid="SignUpDrawer__ErrorMessages" className={errorMessageStyles}>
                        <StyledWarningText>
                            {errMessages.map((error, idx) => <li key={idx}>{error}</li>)}
                        </StyledWarningText>
                    </div>}
                <Button data-testid="SignUpModal__CreateButton" disabled={!isValid()} onClick={onSignup} size={embedded ? 'medium' : 'large'} className={cx(buttonSubmitStyles, embedded ? cx() : embeddedButtonStyles)}>
                    {locales.signup.createAccount}
                </Button>
                <div className={loginContainerStyles} data-testid="Signup__LoginLink">
                    <span className="typography caption">
                        Already a member?&nbsp;
                        <Link onClick={onLogin ?? openAuthenticationDrawer} actionLink mfc className={loginStyles}>
                            {locales.login.login}
                        </Link>
                    </span>
                </div>
            </form>
        </div>;
});
export default SignUp;